/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IProcessedStyleSet, mergeStyleSets } from '@fluentui/merge-styles';
import { ICommandBar } from '@fluentui/react/lib/CommandBar';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/utilities';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { defineMessages } from 'react-intl';
import { CluIntentCreationModal } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentCreationModal';
import { CluIntentsCommandBar } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentsCommandBar';
import { CluIntentsTable } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentsTable';
import { OrchestrationInfo } from 'src/clu/core/stores/CluIntentStore';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logButtonSuccessfulClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CluIntentDto = { intentName: string; labelsCount: number; entities: string[]; orchestrationInfo: OrchestrationInfo };
export type CluIntentsListProps = {
    containerStyles?: IProcessedStyleSet<IComponentStyles<IStackSlots>>;
};

const messages = defineMessages({
    deleteIntentsTitle: {
        id: 'clu.cluIntentsList.deleteIntentsTitle',
        defaultMessage: 'Delete selected intent(s)?'
    },
    deleteIntentsBody: {
        id: 'clu.cluIntentsList.deleteIntentsBody',
        defaultMessage: 'Are you sure you want to delete the selected intent(s)?'
    },
    emptyStateMessage: {
        id: 'clu.cluIntentsList.emptyStateMessage',
        defaultMessage: 'No Intents created yet.'
    },
    createNewIntentLink: {
        id: 'clu.cluIntentsList.createNewIntentLink',
        defaultMessage: 'Create a new Intent.'
    }
});

const intentsListContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { overflow: 'hidden' }
});

const defaultModalsState = { isCreateModalHidden: true, isDeleteModalHidden: true };

export const CluIntentsList = observer((props: CluIntentsListProps) => {
    const { containerStyles } = props;
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const commandBarRef = React.useRef<ICommandBar>();
    const { rootStore, dispatcher } = useGlobalState();
    const { selection, selectedItems } = useSelection<CluIntentDto>();
    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const [sortKey, setSortKey] = React.useState<SortKeyType<CluIntentDto>>({ key: 'intentName', order: 'asc' });
    const [listItems, setListItems] = React.useState<CluIntentDto[]>([]);
    const [intentsTracker, setIntentsTracker] = React.useState(rootStore.currentCluProjectStore.intentStoreHouse.tracker);
    const [isInEmptyState, setIsInEmptyState] = React.useState(false);

    React.useEffect(() => {
        selection.canSelectItem = intent => (intent as CluIntentDto).intentName !== LuisConstants.Luis.NoneIntentName;
    }, []);

    const tracker = useProgressTracker({
        onComplete: () => {
            selection.setAllSelected(false);
            setModalsState({ isDeleteModalHidden: true });
        }
    });

    const { serviceName } = getPortalFromUrl();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const currentProject = rootStore.currentCluProjectStore;
    const appliedContainerStyles = mergeStyleSets(intentsListContainerStyles, containerStyles);

    useEffect(() => {
        const { items: intents, tracker: storedIntentsTracker } = currentProject.intentStoreHouse;
        const { entitiesPerIntent, labelsPerIntent } = currentProject;
        const intentsColumns = intents.map<CluIntentDto>(i => ({
            // You have to provide a key for each item to let react handle the row updates caching stuff, it fixes this bug 14850027.
            key: i.name,
            intentName: i.name,
            orchestrationInfo: i.orchestrationInfo,
            entities: entitiesPerIntent.get(i.name),
            labelsCount: labelsPerIntent.get(i.name) ?? 0
        }));

        const sortedIntentsColumns = [...intentsColumns].sort((a, b) => {
            const aValue = a[sortKey.key];
            const bValue = b[sortKey.key];
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortKey.order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            return sortKey.order === 'asc' ? String(aValue).localeCompare(String(bValue)) : String(bValue).localeCompare(String(aValue));
        });

        setIsInEmptyState(storedIntentsTracker.isCompleted && !intentsColumns.length);
        setIntentsTracker(storedIntentsTracker);

        let filteredIntents = [...sortedIntentsColumns];
        if (query) {
            filteredIntents = filteredIntents.filter(i => i.intentName.indexOf(query) > -1);
        }
        setListItems(filteredIntents);
    }, [sortKey, currentProject.intentStoreHouse.items.length, query]);

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof CluIntentDto, order: column.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const handleDeleteIntents = () => {
        dispatcher.dispatch('deleteCluIntents', { intentNames: selectedItems.map(intent => intent.intentName), tracker, toastData: {} });
        logButtonSuccessfulClick(serviceName, 'Schema', 'DeleteIntent', 'Intents', currentProject.id);
    };

    const emptyStateMessage = (
        <>
            {localize(messages.emptyStateMessage)}&nbsp;
            <RBACTooltip
                hidden={canWrite}
                displayedUserRole={displayedUserRole}
                data-automation-id="createNewIntentLinkLanguageToolTip"
                componentSurroundedByToolTip={
                    <Link disabled={!canWrite} onClick={() => setModalsState({ isCreateModalHidden: false })}>
                        {localize(messages.createNewIntentLink)}
                    </Link>
                }
            />
        </>
    );

    const handleAddIntent = () => {
        setModalsState({ isCreateModalHidden: false });
        logButtonClick(serviceName, 'Schema', 'AddIntent', 'Intents', currentProject.id);
    };

    const handleDeleteIntent = () => {
        setModalsState({ isDeleteModalHidden: false });
        logButtonClick(serviceName, 'Schema', 'DeleteIntent', 'Intents', currentProject.id);
    };

    const handleSubmitAddIntent = () => {
        setModalsState({ isCreateModalHidden: true });
        logButtonSuccessfulClick(serviceName, 'Schema', 'AddIntent', 'Intents', currentProject.id);
    };

    return (
        <Stack verticalFill grow styles={appliedContainerStyles}>
            <CluIntentsCommandBar
                query={query}
                onAdd={handleAddIntent}
                onQueryChange={setQuery}
                componentRef={commandBarRef}
                selectedItems={selectedItems}
                onDelete={handleDeleteIntent}
            />

            {isInEmptyState ? (
                <EmptyStateView message={emptyStateMessage} />
            ) : (
                <CluIntentsTable
                    items={listItems}
                    sortKey={sortKey}
                    selection={selection}
                    onSortColumn={onSortColumn}
                    overallItemsCount={listItems.length}
                    areItemsFiltered={Boolean(query)}
                    isLoading={intentsTracker.isRunning}
                />
            )}

            <CluIntentCreationModal
                onSubmit={handleSubmitAddIntent}
                hidden={modalsState.isCreateModalHidden}
                onDismiss={() => {
                    setModalsState({ isCreateModalHidden: true });
                    commandBarRef.current.focus();
                }}
                enableIntentLinking={currentProject.projectType === 'Orchestration'}
            />
            <DeletePromptModal
                disabled={tracker.isRunning}
                onDelete={handleDeleteIntents}
                hidden={modalsState.isDeleteModalHidden}
                dialogContentProps={{ title: localize(messages.deleteIntentsTitle) }}
                onDismiss={() => setModalsState({ isDeleteModalHidden: true })}>
                {localize(messages.deleteIntentsBody)}
            </DeletePromptModal>
        </Stack>
    );
});
